import {Routes} from "@angular/router";
import {AuthGuard} from "./guards/auth.guard";
import {FeatureCanLoadGuard} from "./guards/feature-can-load.guard";
import {TermsAndConditionsComponent} from "./Pages/terms-and-conditions/terms-and-conditions.component";
import {ActualURLPaths, FeaturesPaths, RootRoutePaths} from "./models/routes-constants";
import {RegistrationRedirectGuard} from "./guards/registration-redirect.guard";
import {adminGuard} from "./guards/admin.guard";
import {ensureSocketConnectedGuard} from "./guards/ensure-socket-connected.guard";

export const appRoutes: Routes = [
    {
        path: RootRoutePaths.ENTITY_COMMENTS,
        loadComponent: () =>
            import("./modules/entity-comments/components/task-comments/task-comments.component").then(
                (c) => c.TaskCommentsComponent,
            ),
        canActivate: [AuthGuard],
    },
    {
        path: FeaturesPaths.CONVERSATION,
        loadComponent: () =>
            import("./modules/conversation/conversation-home.component").then((m) => m.ConversationHomeComponent),
        canActivate: [AuthGuard, ensureSocketConnectedGuard],
        canLoad: [FeatureCanLoadGuard],
    },
    {
        path: FeaturesPaths.USER_PROFILE,
        loadComponent: () =>
            import("./modules/user/user-preference/user-preference.component").then((m) => m.UserPreferenceComponent),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.LEAVE_WORKFLOW,
        loadComponent: () =>
            import("./modules/leave/components/leaves-workflow.component").then((m) => m.LeavesWorkflowComponent),
        canActivate: [AuthGuard],
        canLoad: [FeatureCanLoadGuard],
    },
    {
        path: RootRoutePaths.OVERTIME_WORKFLOW,
        loadComponent: () =>
            import("./modules/overtime/components/overtime-home/overtime-home.component")
                .then((m) => m.OvertimeHomeComponent),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.RELEASES_HOME,
        loadComponent: () => import("./modules/release/components/release-home/release-home.component").then((r) => r.ReleaseHomeComponent),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.RELEASES_HOME + "/:releaseId",
        loadComponent: () =>
            import("./modules/release/components/view-release/view-release.component").then(
                (r) => r.ViewReleaseComponent,
            ),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.ADMIN,
        loadChildren: () => import("./modules/admin/admin-routes").then((r) => r.adminRoutes),
        canActivate: [AuthGuard],
    },
    // Document Module - Uses dedicated document layout (no sidebar)
    {
        path: RootRoutePaths.DOCUMENTS,
        loadChildren: () => import("./modules/document/document.routes").then((r) => r.documentRoutes),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.TASK_MANAGEMENT,
        loadChildren: () =>
            import("./modules/task-management/task-management.routes").then((m) => m.taskManagementRoutes),
        canActivate: [AuthGuard],
    },
    {
        path: ActualURLPaths.MY_MEETINGS,
        loadComponent: () =>
            import("./modules/meetings-workflow/meetings/meetings.component").then((m) => m.MeetingsComponent),
        pathMatch: "full",
        canActivate: [AuthGuard],
        canLoad: [FeatureCanLoadGuard],
    },
    {
        path: RootRoutePaths.TIMELINE_VIEW,
        loadComponent: () =>
            import("./modules/timeline-view/timeline/timeline.component").then((m) => m.TimelineComponent),
        canActivate: [AuthGuard],
        canLoad: [FeatureCanLoadGuard],
    },
    {
        path: RootRoutePaths.ATTENDANCE,
        loadComponent: () => import("./modules/attendance/attendance.component").then((m) => m.AttendanceComponent),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.GEO_FENCE_ATTENDANCE,
        loadComponent: () => import("./modules/geo-fence-attendance/geo-fence-attendance.component").then((m) => m.GeoFenceAttendanceComponent),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.TEMPLATES,
        loadComponent: () =>
            import("./modules/templates/task-templates/task-templates.component").then((m) => m.TaskTemplatesComponent),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.USER_ALERTS,
        // alertRoutes
        loadChildren: () => import("./modules/user-alerts/user-alerts.routes").then((r) => r.alertRoutes),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.TASKMASTER,
        loadComponent: () => import("./modules/task-master/task-master.component").then((m) => m.TaskMasterComponent),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.WORKFLOW,
        loadChildren: () =>
            import("./modules/workflow/workflow.routes")
                .then(r => r.workflowRoutes),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.PERFORMANCE_FEEDBACKS,
        loadComponent: () =>
            import("./modules/performance-feedbacks/work-items-feedbacks/work-items-feedbacks.component").then(
                (m) => m.WorkItemsFeedbacksComponent,
            ),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.EPICS_HOME,
        loadChildren: () => import("./modules/epic/epic.routes").then((r) => r.epicRoutes),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.SUPER_ADMIN,
        loadChildren: () => import("./modules/super-admin/super-admin-routes").then((r) => r.superAdminRoutes),
        canActivate: [adminGuard],
    },
    {
        path: RootRoutePaths.GEO_FENCE_ADMIN,
        loadChildren: () => import("./modules/geo-fence-admin/geo-fence-admin.routes").then((r) => r.geoFenceAdminRoutes),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.MEET,
        loadComponent: () =>
            import("./modules/video-meeting/jitsi-meeting.component").then((m) => m.JitsiMeetingComponent),
        pathMatch: "full",
    },
    {
        path: RootRoutePaths.ACCOUNT,
        canActivate: [AuthGuard, ensureSocketConnectedGuard],
        children: [
            {
                path: RootRoutePaths.BOARD_VIEW,
                loadComponent: () =>
                    import("./modules/taskboard/task-board.component").then(
                        (m) => m.TaskBoardComponent,
                    ),
                pathMatch: "full",
            },
            {
                path: RootRoutePaths.DASHBOARD,
                loadComponent: () => import("./components/dashboard/Components/new-app-dashboard/new-app-dashboard.component").then(
                    (m) => m.NewAppDashboardComponent,
                ),
                pathMatch: "full"
            },
            {path: '', redirectTo: RootRoutePaths.DASHBOARD, pathMatch: 'full'}, // Default redirect to dashboard
        ],
    },
    {
        path: RootRoutePaths.STICKY_NOTES,
        loadComponent: () =>
            import("./modules/sticky-notes/sticky-notes.component").then((m) => m.StickyNotesComponent),
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.REGISTRATION,
        canActivate: [RegistrationRedirectGuard],
        loadComponent: () =>
            import("./components/Shared/redirecting/redirecting.component").then((m) => m.RedirectingComponent),
    },
    {
        path: RootRoutePaths.AUTH,
        loadChildren: () => import("./modules/auth/auth.routes").then((r) => r.authRoutes),
    },

    {
        path: RootRoutePaths.ROOT,
        loadComponent: () => import("../app/components/dashboard/Components/new-app-dashboard/new-app-dashboard.component").then(
            (m) => m.NewAppDashboardComponent,
        ),
        pathMatch: "full",
        canActivate: [AuthGuard],
    },
    {
        path: RootRoutePaths.HOME,
        loadComponent: () => import("./Pages/home-page/home-page.component").then((m) => m.HomePageComponent),
        pathMatch: "full",
    },
    {
        path: RootRoutePaths.PRIVACY_POLICY,
        loadComponent: () =>
            import("./Pages/privacy-policy/privacy-policy.component").then((m) => m.PrivacyPolicyComponent),
        pathMatch: "full",
    },
    {path: RootRoutePaths.TERMS_AND_CONDITIONS, component: TermsAndConditionsComponent, pathMatch: "full"},
    {
        path: RootRoutePaths.ADD_TO_TEAM_BY_INVITE,
        loadComponent: () =>
            import("./modules/core/components/team-invite/team-invite.component").then((m) => m.TeamInviteComponent),
        pathMatch: "full",
    },
    {
        path: RootRoutePaths.TIMESHEET + "/" + FeaturesPaths.TEAM_TIMESHEET,
        loadComponent: () =>
            import("./modules/timesheet/team-time-sheet/team-time-sheet.component").then(
                (m) => m.TeamTimeSheetComponent,
            ),
        canActivate: [AuthGuard],
        canLoad: [FeatureCanLoadGuard],
    },
    {
        path: RootRoutePaths.TIMESHEET + "/" + FeaturesPaths.MY_TIMESHEET,
        loadComponent: () =>
            import("./modules/timesheet/myTimeSheet/mytimesheet.component").then(
                (m) => m.MytimesheetComponent,
            ),
        canActivate: [AuthGuard],
        canLoad: [FeatureCanLoadGuard],
    },
    // Attendance Module - Uses your existing app shell/layout
    {
        path: 'geo-fence-admin',
        children: [
            {path: '', redirectTo: 'dashboard', pathMatch: 'full'},
            {
                path: 'dashboard',
                loadComponent: () => import("./modules/geo-fence-admin/components/geo-fence-admin-panel-dashboard/geo-fence-admin-panel-dashboard.component").then(m => m.GeoFenceAdminPanelDashboardComponent),
            },
        ]
    },
    {
        path: ActualURLPaths.SETTINGS,
        canActivate: [AuthGuard],
        loadChildren: () => import("./components/nav-bar/settings.routes").then((r) => r.settingsRoutes),
    },
    {path: '**', redirectTo: RootRoutePaths.HOME} // Wildcard route for a 404 page, can be customized later
];
